// RadicalSimulation.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { Icons, getCardTextColor, getLetters } from "./RadicalHelpers";

const MiniCard = ({ card, compact = false, onClick, selected = false }) => {
    const cardColor = getCardTextColor(card.value);
    const isReversed = Boolean(card.isReversed);
    const displayedCode = card.dealingCode || card.code;

    return (
        <Paper
            variant="outlined"
            onClick={onClick}
            title={`${card.value} · ${card.code}${
                isReversed ? ` → ${displayedCode}` : ""
            }`}
            sx={{
                width: compact ? 72 : 88,
                minWidth: compact ? 72 : 88,
                height: compact ? 42 : 52,
                px: 0.75,
                py: 0.4,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                borderRadius: 1.2,
                overflow: "hidden",
                bgcolor: isReversed ? "#5B1E24" : "background.paper",
                borderColor: selected
                    ? "text.primary"
                    : isReversed
                      ? "#7E2D36"
                      : "divider",
                borderWidth: selected ? 2 : 1,
                cursor: onClick ? "pointer" : "default",
                userSelect: "none",
                transition:
                    "transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease",
                "&:hover": onClick
                    ? {
                          transform: "translateY(-1px)",
                          boxShadow: 2,
                      }
                    : undefined,
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 700,
                    lineHeight: 1.1,
                    fontSize: compact ? 12 : 13,
                    color: isReversed ? "#FFFFFF" : cardColor,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {card.value}
            </Typography>

            <Typography
                variant="caption"
                sx={{
                    lineHeight: 1,
                    fontSize: 10,
                    fontFamily: "monospace",
                    color: isReversed ? "#F6F6F6" : "text.secondary",
                }}
            >
                {isReversed ? `${card.code} → ${displayedCode}` : displayedCode}
            </Typography>
        </Paper>
    );
};

const MixedDeckView = ({ cards, onCardToggle, orden = "" }) => {
    const rotations = [-6, -3, 0, 3, 6];

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "flex-start",
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 700,
                    alignSelf: "center",
                    mr: 0.5,
                }}
            >
                Top {orden}
            </Typography>

            {cards.map((card, index) => (
                <Box
                    key={`${card.id}-mixed`}
                    sx={{
                        transform: `rotate(${rotations[index % rotations.length]}deg)`,
                        mt: index % 3 === 0 ? 0.5 : 0,
                    }}
                >
                    <MiniCard
                        card={card}
                        compact
                        onClick={() => onCardToggle(card.id)}
                    />
                </Box>
            ))}

            <Typography
                variant="body2"
                sx={{
                    fontWeight: 700,
                    alignSelf: "center",
                    mr: 0.5,
                }}
            >
                Bottom {orden}
            </Typography>
        </Box>
    );
};

const PacketPile = ({
    passNumber,
    letter,
    pictogram,
    cards,
    selectedTargets,
    onTableSelection,
}) => {
    const stackOffset = 24;
    const cardHeight = 42;
    const headerHeight = 34;
    const bottomPadding = 10;
    const pileSelectionKey = `pile-${passNumber}-${letter}`;

    const pileHeight =
        cards.length > 0
            ? headerHeight +
              cardHeight +
              (cards.length - 1) * stackOffset +
              bottomPadding
            : 72;

    return (
        <Paper
            variant="outlined"
            onClick={(event) =>
                onTableSelection?.(event, pileSelectionKey, cards)
            }
            sx={{
                p: 0.75,
                width: 118,
                minWidth: 118,
                height: pileHeight,
                display: "flex",
                flexDirection: "column",
                borderRadius: 1.5,
                overflow: "hidden",
                borderColor: selectedTargets[pileSelectionKey]
                    ? "text.primary"
                    : "divider",
                borderWidth: selectedTargets[pileSelectionKey] ? 2 : 1,
                boxSizing: "border-box",
                cursor: "pointer",
                transition: "border-color 120ms ease",
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 700,
                    textAlign: "center",
                    mb: 0.75,
                    fontSize: 18,
                    lineHeight: 1.1,
                }}
            >
                {pictogram ? `${pictogram} ` : ""}
                {letter}{" "}
                <span style={{ fontSize: 10, color: "text.secondary" }}>
                    ({cards.length})
                </span>
            </Typography>

            <Box
                sx={{
                    position: "relative",
                    flex: 1,
                    minHeight: 20,
                }}
            >
                {cards.map((card, index) => {
                    const cardSelectionKey = `card-${card.id}`;

                    return (
                        <Box
                            key={`${card.id}-${letter}-${index}`}
                            sx={{
                                position: "absolute",
                                top: index * stackOffset,
                                left: "50%",
                                transform: "translateX(-50%)",
                            }}
                        >
                            <MiniCard
                                card={card}
                                compact
                                selected={Boolean(
                                    selectedTargets[cardSelectionKey],
                                )}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onTableSelection?.(
                                        event,
                                        cardSelectionKey,
                                        [card],
                                    );
                                }}
                            />
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
};

const PilesStepView = ({
    step,
    letters,
    pictograms,
    selectedTargets,
    onTableSelection,
}) => {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns:
                    "repeat(auto-fit, minmax(118px, max-content))",
                gap: 1,
                alignItems: "start",
            }}
        >
            {letters.map((letter) => (
                <PacketPile
                    key={`${step.passNumber}-${letter}`}
                    passNumber={step.passNumber}
                    letter={letter}
                    pictogram={pictograms?.[letter] || ""}
                    cards={step.piles[letter] || []}
                    selectedTargets={selectedTargets}
                    onTableSelection={onTableSelection}
                />
            ))}
        </Box>
    );
};

const OrderedDeckView = ({ cards, orden = "" }) => {
    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
                alignItems: "flex-start",
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 700,
                    alignSelf: "center",
                    mr: 0.5,
                }}
            >
                Top {orden}
            </Typography>

            {cards.map((card, index) => (
                <Box
                    key={`${card.id}-final-${index}`}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.25,
                    }}
                >
                    <MiniCard card={card} compact />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: 9 }}
                    >
                        {index + 1}
                    </Typography>
                </Box>
            ))}

            <Typography
                variant="body2"
                sx={{
                    fontWeight: 700,
                    alignSelf: "center",
                    ml: 0.5,
                }}
            >
                Bottom {orden}
            </Typography>
        </Box>
    );
};

const SectionBlock = ({
    title,
    subtitle,
    children,
    bold,
    defaultExpanded = false,
}) => {
    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            disableGutters
            sx={{
                borderRadius: 1.5,
                "&:before": { display: "none" },
                border: 1,
                borderColor: bold ? "text.primary" : "divider",
                boxShadow: "none",
            }}
        >
            <AccordionSummary expandIcon={<Icons.ExpandMore />}>
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {title}
                    </Typography>

                    {subtitle && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                whiteSpace: "pre-line",
                                lineHeight: 1,
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Stack spacing={1}>{children}</Stack>
            </AccordionDetails>
        </Accordion>
    );
};

const RadicalSimulation = ({
    rows,
    cardsByCode,
    packets,
    deals,
    pictograms,
    deckOrientation = "cara",
    onTableHighlightChange,
}) => {
    const [reversedCardsById, setReversedCardsById] = useState({});
    const [shuffleWeightsById, setShuffleWeightsById] = useState({});
    const [selectedTargets, setSelectedTargets] = useState({});

    useEffect(() => {
        onTableHighlightChange?.(Object.values(selectedTargets).flat());
    }, [selectedTargets, onTableHighlightChange]);

    const baseInitialDeck = useMemo(() => {
        return rows
            .map((row) => ({
                id: row.finalCode,
                code: row.finalCode,
                value: cardsByCode[row.finalCode] || "",
            }))
            .filter((card) => String(card.value).trim() !== "")
            .map((card) => ({
                ...card,
                shuffleWeight: shuffleWeightsById[card.id] ?? 0,
            }))
            .sort((a, b) => {
                if (a.shuffleWeight !== b.shuffleWeight) {
                    return a.shuffleWeight - b.shuffleWeight;
                }

                return a.code.localeCompare(b.code);
            })
            .map(({ shuffleWeight, ...card }) => card);
    }, [rows, cardsByCode, shuffleWeightsById]);

    const initialDeck = useMemo(() => {
        return baseInitialDeck.map((card) => {
            const isReversed = Boolean(reversedCardsById[card.id]);

            return {
                ...card,
                isReversed,
                dealingCode: isReversed
                    ? String(card.code || "")
                          .split("")
                          .reverse()
                          .join("")
                    : card.code,
            };
        });
    }, [baseInitialDeck, reversedCardsById]);

    const simulation = useMemo(() => {
        const letters = getLetters(packets);
        const steps = [];
        let currentDeck = initialDeck;

        for (let passIndex = 0; passIndex < deals; passIndex += 1) {
            const piles = letters.reduce((acc, letter) => {
                acc[letter] = [];
                return acc;
            }, {});

            currentDeck.forEach((card) => {
                const pileLetter = card.dealingCode?.[passIndex];

                if (piles[pileLetter]) {
                    piles[pileLetter].push(card);
                }
            });

            const collectedDeck = [];

            letters.forEach((letter) => {
                const pile = [...piles[letter]];

                while (pile.length > 0) {
                    collectedDeck.push(pile.pop());
                }
            });

            steps.push({
                passNumber: passIndex + 1,
                piles,
                collectedDeck,
            });

            currentDeck = collectedDeck;
        }

        return {
            letters,
            initialDeck,
            steps,
            finalDeck: currentDeck,
        };
    }, [initialDeck, packets, deals]);

    const finalDeck = useMemo(() => {
        if (deckOrientation === "dorso") {
            return simulation.finalDeck;
        }

        return [...simulation.finalDeck].reverse();
    }, [simulation.finalDeck, deckOrientation]);

    const finalTitle =
        deckOrientation === "dorso"
            ? "Final, sin voltear"
            : "Final, tras voltear";

    const finalSubtitle =
        deckOrientation === "dorso"
            ? "Orden resultante al dejar la baraja de dorso después de repartir y recoger"
            : "Orden resultante al voltear la baraja después de repartir y recoger";

    const finalOrderLabel = deckOrientation === "dorso" ? "(dorso)" : "(cara)";

    const handleCardToggle = (cardId) => {
        setReversedCardsById((prev) => ({
            ...prev,
            [cardId]: !prev[cardId],
        }));
    };
    const handleTableSelection = (event, target, cards) => {
        const multiple = event.ctrlKey || event.metaKey;

        setSelectedTargets((prev) => {
            if (!multiple) {
                return Object.keys(prev).length === 1 && prev[target]
                    ? {}
                    : { [target]: cards };
            }

            const next = { ...prev };

            if (next[target]) {
                delete next[target];
            } else {
                next[target] = cards;
            }

            return next;
        });
    };
    const assignedCount = simulation.initialDeck.length;

    if (assignedCount === 0) {
        return (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                    No hay cartas asignadas para simular.
                </Typography>
            </Paper>
        );
    }

    return (
        <Stack spacing={1.5}>
            <SectionBlock
                title="Mazo inicial"
                subtitle={`${assignedCount} cartas asignadas.\nHaz click en una carta para repartirla con el código invertido.`}
            >
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                        setShuffleWeightsById(
                            Object.fromEntries(
                                baseInitialDeck.map((card) => [
                                    card.id,
                                    Math.random(),
                                ]),
                            ),
                        )
                    }
                    sx={{
                        alignSelf: "flex-start",
                        textTransform: "none",
                    }}
                >
                    Mezclar
                </Button>

                <MixedDeckView
                    cards={simulation.initialDeck}
                    onCardToggle={handleCardToggle}
                    orden={"(dorso)"}
                />
            </SectionBlock>

            {simulation.steps.map((step) => (
                <SectionBlock
                    key={`step-${step.passNumber}`}
                    title={`Dada ${step.passNumber}`}
                    subtitle={`Reparto por la letra ${step.passNumber} del código`}
                >
                    <PilesStepView
                        step={step}
                        letters={simulation.letters}
                        pictograms={pictograms}
                        selectedTargets={selectedTargets}
                        onTableSelection={handleTableSelection}
                    />
                </SectionBlock>
            ))}

            <SectionBlock
                title="Tras recoger"
                subtitle="Orden resultante tras repartir y recoger todas las dadas de izquierda a derecha"
            >
                <OrderedDeckView
                    cards={simulation.finalDeck}
                    orden={"(dorso)"}
                />
            </SectionBlock>

            <SectionBlock title={finalTitle} subtitle={finalSubtitle} bold>
                <OrderedDeckView cards={finalDeck} orden={finalOrderLabel} />
            </SectionBlock>
        </Stack>
    );
};

export default RadicalSimulation;
